import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { fetchLeaderboard, fetchLeaderboardPlay, fetchPublicLeaderboard } from '../api/leaderboard';
import { fetchTodayPuzzles, isLockedPuzzle, type Difficulty } from '../api/puzzles';
import { loadLocalLife, guestDayState } from '../game/local-persistence';
import { isDayClaimed } from '../api/anon';
import { Board } from '../components/board/Board';
import { LetterTiles } from '../components/ui/LetterTiles';
import { londonToday } from '../lib/gameDate';
import { originOf } from '../lib/back-origin';

const TABS: Array<{ difficulty: Difficulty; active: string }> = [
  { difficulty: 'EASY', active: 'bg-emerald-800 text-white border-emerald-950' },
  { difficulty: 'MEDIUM', active: 'bg-blue-800 text-white border-blue-950' },
  { difficulty: 'HARD', active: 'bg-red-800 text-white border-red-950' },
];

const fmt = (n: number) => n.toLocaleString('en-GB');

const DIFFICULTIES: Difficulty[] = ['EASY', 'MEDIUM', 'HARD'];

/** `?d=hard` → 'HARD'. Anything missing or unrecognised falls back to EASY,
 *  so a hand-typed or stale URL still renders a leaderboard. */
function difficultyFromParam(raw: string | null): Difficulty {
  const upper = (raw ?? '').toUpperCase();
  return (DIFFICULTIES.find((d) => d === upper) ?? 'EASY') as Difficulty;
}

export default function Leaderboard() {
  const { user, loading } = useAuth();
  const location = useLocation();
  // The selected board lives in the URL, not component state. Without that
  // there is no address meaning "the hard leaderboard", so returning here
  // from a profile — by link or by browser back — always dumped the player
  // back on EASY. It also makes a given board shareable and refresh-proof.
  const [searchParams, setSearchParams] = useSearchParams();
  const difficulty = difficultyFromParam(searchParams.get('d'));
  const setDifficulty = (next: Difficulty) => {
    // `replace` so flicking through tabs doesn't stack history entries — the
    // back button should leave the leaderboard, not walk the tabs you tried.
    setSearchParams({ d: next.toLowerCase() }, { replace: true });
  };
  const [openLifeId, setOpenLifeId] = useState<string | null>(null);
  const date = londonToday();

  const boardQuery = useQuery({
    queryKey: ['leaderboard', date, difficulty],
    queryFn: () => fetchLeaderboard(date, difficulty),
    enabled: !!user,
  });
  const playQuery = useQuery({
    queryKey: ['leaderboard-play', date, difficulty, openLifeId],
    queryFn: () => fetchLeaderboardPlay(date, difficulty, openLifeId!),
    enabled: !!user && !!openLifeId,
  });

  // A guest's score for the selected tab comes from their own local save.
  const todayQuery = useQuery({
    queryKey: ['puzzles', 'today'],
    queryFn: fetchTodayPuzzles,
    enabled: !loading && !user,
  });

  const myScore = (() => {
    // A claimed day's local save is a duplicate of what now lives on the
    // player's account, so it must not drive the teaser — otherwise a
    // signed-out player is told where a score they already own "would" place
    // them. Matches the landing cards, which ignore it for the same reason.
    if (isDayClaimed(date)) return undefined;
    const puzzle = todayQuery.data?.puzzles.find((p) => p.difficulty === difficulty);
    if (!puzzle || isLockedPuzzle(puzzle)) return undefined;
    const local = guestDayState(loadLocalLife(puzzle.id));
    // Distinguish "hasn't played" from "played and scored nothing" — a
    // completed life with 0 points still has a real rank to show.
    return local.state !== 'not_started' ? local.best_score : undefined;
  })();

  const publicQuery = useQuery({
    queryKey: ['leaderboard-public', date, difficulty, myScore ?? null],
    queryFn: () => fetchPublicLeaderboard(date, difficulty, myScore),
    enabled: !loading && !user && !todayQuery.isLoading,
  });

  if (!loading && !user) {
    const data = publicQuery.data;
    const hidden = Math.max(0, (data?.total_players ?? 0) - (data?.top.length ?? 0));
    return (
      <section data-testid="page-leaderboard">
        <header className="text-center">
          <h1 className="leading-none">
            <LetterTiles word="TOP FIVE" size="lg" label="Top five" />
          </h1>
          <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-600">
            Today's best plays · anonymous names only
          </p>
        </header>

        <div role="tablist" aria-label="Difficulty" className="mt-5 flex justify-center gap-2">
          {TABS.map((t) => (
            <button
              key={t.difficulty}
              role="tab"
              aria-selected={difficulty === t.difficulty}
              onClick={() => setDifficulty(t.difficulty)}
              className={`rounded-md border-b-4 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] ${
                difficulty === t.difficulty ? t.active : 'border-stone-300 bg-white text-stone-600'
              }`}
            >
              {t.difficulty}
            </button>
          ))}
        </div>

        {publicQuery.isLoading ? (
          <p className="mt-6 text-stone-500" role="status">
            Loading today's leaderboard…
          </p>
        ) : publicQuery.isError || !data ? (
          <p className="mt-6 text-stone-600">
            <Link to="/login" className="font-semibold text-emerald-700 underline">
              Sign in
            </Link>{' '}
            to see today's leaderboards.
          </p>
        ) : (
          <>
            <ol className="mt-5 divide-y divide-tile-edge/60 rounded-lg border border-tile-edge bg-[#fffdf6]">
              {data.top.map((e) => (
                <li key={e.rank} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="w-6 text-sm font-bold tabular-nums text-stone-500">
                    {e.rank}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                    {/* Same real `leaderboard_name` field the signed-in
                        rows use — the public leaderboard endpoint doesn't
                        anonymise it — so linking it to the profile page is
                        exactly as safe here as anywhere else. This is also
                        the conversion path the guest teaser exists for. */}
                    <Link
                      to={`/player/${encodeURIComponent(e.leaderboard_name)}`}
                      state={{ from: originOf(location) }}
                      data-testid={`lb-public-profile-link-${e.rank}`}
                      className="hover:underline"
                    >
                      {e.leaderboard_name}
                    </Link>
                  </span>
                  <span className="text-sm font-bold tabular-nums">{fmt(e.score)}</span>
                </li>
              ))}
            </ol>

            {hidden > 0 ? (
              <p className="mt-2 select-none rounded-md bg-stone-100 py-2 text-center text-sm text-stone-400 blur-[2px]">
                <span aria-hidden="true">▩▩▩▩▩ </span>
                {hidden} more players
                <span aria-hidden="true"> ▩▩▩▩▩</span>
              </p>
            ) : null}

            {data.would_rank !== null ? (
              <div
                data-testid="would-rank-row"
                className="mt-4 rounded-lg border-2 border-dashed border-emerald-700 bg-emerald-50 px-4 py-3 text-center"
              >
                <p className="text-sm text-emerald-900">
                  Your {fmt(myScore ?? 0)} today would place{' '}
                  <span className="font-bold">#{data.would_rank}</span> of {data.total_players}.
                </p>
                <Link
                  to="/register"
                  className="mt-3 block rounded-md border-b-4 border-emerald-950 bg-emerald-800 px-4 py-2.5 text-sm font-bold uppercase tracking-[0.18em] text-white"
                >
                  Create a free account to claim your place
                </Link>
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-tile-edge bg-[#fffdf6] px-4 py-3 text-center">
                <p className="text-sm text-stone-700">
                  Play today's board to see where you'd land.
                </p>
                <Link
                  to="/"
                  className="mt-3 block rounded-md border-b-4 border-emerald-950 bg-emerald-800 px-4 py-2.5 text-sm font-bold uppercase tracking-[0.18em] text-white"
                >
                  Play today's puzzle
                </Link>
              </div>
            )}
          </>
        )}
      </section>
    );
  }

  const data = boardQuery.data;

  return (
    <section data-testid="page-leaderboard">
      <header className="text-center">
        <h1 className="leading-none">
          <LetterTiles word="TOP TEN" size="lg" label="Top ten" />
        </h1>
        <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-600">
          Today's best plays · anonymous names only
        </p>
      </header>

      <div role="tablist" aria-label="Difficulty" className="mt-5 flex justify-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.difficulty}
            role="tab"
            aria-selected={difficulty === t.difficulty}
            onClick={() => setDifficulty(t.difficulty)}
            className={`rounded-md border-b-4 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] ${
              difficulty === t.difficulty
                ? t.active
                : 'border-tile-edge bg-tile-face text-stone-700'
            }`}
          >
            {t.difficulty.toLowerCase()}
          </button>
        ))}
      </div>

      {boardQuery.isLoading || loading ? (
        <p role="status" className="mt-6 text-center text-stone-500">
          Loading leaderboard…
        </p>
      ) : boardQuery.isError ? (
        <p role="alert" className="mt-6 text-center text-red-600">
          Couldn't load the leaderboard — try again shortly.
        </p>
      ) : data && data.entries.length === 0 ? (
        <p className="mt-8 text-center text-stone-600">No plays yet today — be the first.</p>
      ) : data ? (
        <>
          {!data.can_view_plays ? (
            <p
              data-testid="lb-plays-locked"
              className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-center text-xs text-amber-800"
            >
              🔒 Boards unlock once you finish your run — win the day or use all three lives. Scores
              are always visible.
            </p>
          ) : null}
          <ol className="mt-5 space-y-1.5">
            {data.entries.map((e) => (
              <li key={e.life_id} className="flex items-stretch gap-1.5">
                <button
                  type="button"
                  data-testid={`lb-row-${e.rank}`}
                  onClick={() => (data.can_view_plays ? setOpenLifeId(e.life_id) : undefined)}
                  disabled={!data.can_view_plays}
                  className={`flex min-w-0 flex-1 items-center gap-3 rounded-lg border px-3 py-2.5 text-left shadow-[0_2px_0_rgba(217,199,154,0.9)] ${
                    e.is_me ? 'border-emerald-700 bg-emerald-50' : 'border-tile-edge bg-[#fffdf6]'
                  } ${data.can_view_plays ? 'hover:bg-tile-face/40' : 'cursor-default'}`}
                >
                  <span className="w-8 text-center text-lg font-bold tabular-nums text-stone-700">
                    {e.rank}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-semibold text-stone-900">
                    {e.leaderboard_name}
                    {e.is_me ? <span className="ml-1 text-emerald-800">(you)</span> : null}
                  </span>
                  {e.won ? (
                    <span className="rounded-sm bg-emerald-800 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      Won ✓
                    </span>
                  ) : null}
                  <span className="text-xs text-stone-500">{e.moves_used} mv</span>
                  <span className="w-14 text-right text-xl font-bold tabular-nums">{e.score}</span>
                </button>
                {/* A profile discloses nothing about today's boards (no
                    board content, only season/all-time stats), so this link
                    is exempt from the can_view_plays gate above — it's
                    always active, unlike the button beside it. */}
                <Link
                  to={`/player/${encodeURIComponent(e.leaderboard_name)}`}
                  state={{ from: originOf(location) }}
                  data-testid={`lb-profile-link-${e.rank}`}
                  aria-label={`View ${e.leaderboard_name}'s profile`}
                  className="flex shrink-0 items-center justify-center rounded-lg border border-tile-edge bg-[#fffdf6] px-2.5 text-stone-500 shadow-[0_2px_0_rgba(217,199,154,0.9)] hover:bg-tile-face/40 hover:text-emerald-800"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6" />
                  </svg>
                </Link>
              </li>
            ))}
          </ol>

          <p
            data-testid="lb-own-rank"
            className="mt-5 rounded-lg border border-tile-edge bg-[#fffdf6] px-4 py-3 text-center text-sm font-semibold text-stone-800 shadow-[0_2px_0_rgba(217,199,154,0.9)]"
          >
            {data.me
              ? `You · ${data.me.score} pts · ${fmt(data.me.rank)} / ${fmt(data.total_players)}`
              : 'Finish a life to get ranked.'}
          </p>
        </>
      ) : null}

      {openLifeId ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Leaderboard play"
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setOpenLifeId(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-5 text-center shadow-xl"
            onClick={(ev) => ev.stopPropagation()}
          >
            {playQuery.isLoading ? (
              <p role="status" className="text-stone-500">
                Loading play…
              </p>
            ) : playQuery.isError || !playQuery.data ? (
              <p role="alert" className="text-red-600">
                Couldn't load this play.
              </p>
            ) : (
              <>
                <h2 className="text-lg font-bold">{playQuery.data.leaderboard_name}</h2>
                <p className="mt-0.5 text-sm text-stone-600">
                  {playQuery.data.score} pts · {playQuery.data.moves_used} moves
                  {playQuery.data.won ? ' · won' : ''}
                </p>
                <div className="mx-auto mt-3 max-w-64">
                  <Board
                    board={playQuery.data.board}
                    staged={[]}
                    hidePremiumLabels
                    testIdPrefix="lb-"
                  />
                </div>
                <p className="mt-3 text-sm text-stone-700">
                  {playQuery.data.words.map((w, i) => (
                    <span key={`${w.word}-${i}`}>
                      {i > 0 ? ' · ' : ''}
                      <span className="font-semibold">{w.word}</span> {w.score}
                    </span>
                  ))}
                </p>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpenLifeId(null)}
              className="mt-4 w-full rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
